import React, { createContext, useContext, useState, useEffect, ReactNode, useRef } from "react";
import { client } from "@/lib/api";
import { initializeTrackingWithAuth } from "@/lib/platform-tracking";

/**
 * Authentication Context
 * 
 * Provides authentication state and methods throughout the application.
 * Handles user login/logout, token storage, and authentication persistence.
 * 
 * Features:
 * - Automatic token persistence in localStorage with sessionStorage fallback
 * - Enhanced token verification with retry logic and error distinction  
 * - Global authentication state management
 * - Login/logout methods
 * - Loading states for authentication operations
 * - Graceful fallback handling for temporary network issues
 * - Comprehensive error logging for debugging
 * - Environment-aware API URL detection for all deployment environments
 * 
 * ENHANCED FOR CREDENTIAL MEMORY STABILITY:
 * - Retry mechanism for token verification failures
 * - Distinction between expired tokens vs network errors
 * - Only removes tokens for actual token invalidity
 * - Backup storage mechanisms (sessionStorage fallback)
 * - Token integrity checking before verification
 * - Detailed error logging for debugging credential issues
 * 
 * FIXED FOR LOADING STATE MANAGEMENT:
 * - Ensures setIsLoading(false) is called in ALL code paths
 * - Adds timeout protection to prevent infinite loading states
 * - Simplified retry logic to reduce complexity
 * - Proper cleanup of loading state during retries
 * 
 * CRITICAL FIXES FOR ACCUMULATING AUTHENTICATION:
 * - Moved debouncing state to module level to persist across renders
 * - Added verification instance tracking to prevent conflicts
 * - Immediate cleanup of all verification attempts on new login
 * - Prevents token re-storage from triggering new verification cycles
 * - Added authentication session isolation
 * 
 * ENVIRONMENT-AWARE API URL STRATEGY:
 * - Automatically detects correct API base URL for any environment
 * - Works in local development, Deno Deploy, and any deployment platform
 * - No hardcoded URLs or manual configuration required
 * - Comprehensive debugging for URL resolution issues
 * 
 * UPDATED FOR DEMO SIGNUPS:
 * - Register function now accepts optional role parameter ("user" | "preview")
 * - Supports creating preview users for demo signups
 * - Default role is "user" for normal signups
 */

interface User {
  id: string;
  name: string;
  email: string;
  subscription_status: "free" | "premium";
  role: "user" | "admin" | "preview";
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  register: (name: string, email: string, password: string, role?: "user" | "preview") => Promise<{ success: boolean; error?: string }>;
  logout: () => void;
  isAuthenticated: boolean;
  isAdmin: boolean;
  isPreview: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// CRITICAL FIX: Move verification state to module level to persist across renders
let globalVerificationState = {
  isVerifying: false,
  currentVerificationId: null as string | null,
  pendingTimeouts: new Set<NodeJS.Timeout>(),
  lastVerificationTime: 0
};

/**
 * Environment-aware API base URL detection
 * Automatically works in all deployment environments without configuration
 */
function getApiBaseUrl(): string {
  // Server-side rendering or non-browser environment
  if (typeof window === 'undefined') {
    return ''; // Use relative URLs on server
  }
  
  // Browser environment - use current origin
  // This automatically works for:
  // - Local development (http://localhost:3000)
  // - Deno Deploy (https://your-app.deno.dev)  
  // - Any custom domain (https://yourdomain.com)
  const baseUrl = window.location.origin;
  
  // Log the detected URL for debugging
  console.log('🌐 API Base URL detected:', {
    baseUrl,
    currentURL: window.location.href,
    protocol: window.location.protocol,
    host: window.location.host,
    timestamp: new Date().toISOString()
  });
  
  return baseUrl;
}

/**
 * Enhanced logging utility for credential debugging with timestamps
 */
function logCredentialDebug(operation: string, data: any): void {
  const timestamp = new Date().toISOString();
  console.log(`🔐 [${timestamp}] CREDENTIAL_${operation}:`, JSON.stringify(data, null, 2));
}

/**
 * Enhanced API call debugging utility
 */
function logApiCall(method: string, url: string, additionalData?: any): void {
  const timestamp = new Date().toISOString();
  console.log(`🌐 [${timestamp}] API_CALL:`, {
    method,
    url,
    fullURL: url.startsWith('http') ? url : `${getApiBaseUrl()}${url}`,
    timestamp,
    ...additionalData
  });
}

/**
 * CRITICAL FIX: Clean up all pending verification attempts
 */
function cleanupAllVerification(): void {
  logCredentialDebug("CLEANUP_ALL_VERIFICATION", {
    wasVerifying: globalVerificationState.isVerifying,
    pendingTimeouts: globalVerificationState.pendingTimeouts.size,
    currentId: globalVerificationState.currentVerificationId
  });

  // Clear all pending timeouts
  globalVerificationState.pendingTimeouts.forEach(timeout => {
    clearTimeout(timeout);
  });
  globalVerificationState.pendingTimeouts.clear();

  // Reset verification state
  globalVerificationState.isVerifying = false;
  globalVerificationState.currentVerificationId = null;
}

/**
 * Check if localStorage is available
 */
function isLocalStorageAvailable(): boolean {
  try {
    const test = '__storage_test__';
    localStorage.setItem(test, test);
    localStorage.removeItem(test);
    return true;
  } catch {
    return false;
  }
}

/**
 * Check if sessionStorage is available  
 */
function isSessionStorageAvailable(): boolean {
  try {
    const test = '__session_test__';
    sessionStorage.setItem(test, test);
    sessionStorage.removeItem(test);
    return true;
  } catch {
    return false;
  }
}

/**
 * FIXED: Enhanced token storage WITHOUT triggering verification
 */
function storeToken(token: string, skipVerificationTrigger: boolean = false): void {
  const storageData = {
    token,
    timestamp: Date.now(),
    integrity: token.length,
    skipVerificationTrigger // CRITICAL: Flag to prevent verification loops
  };
  
  const serializedData = JSON.stringify(storageData);
  
  try {
    // Primary storage: localStorage
    if (isLocalStorageAvailable()) {
      localStorage.setItem("auth_token", token);
      localStorage.setItem("auth_token_data", serializedData);
      logCredentialDebug("TOKEN_STORED_LOCAL", { 
        tokenLength: token.length, 
        hasIntegrity: true,
        skipVerificationTrigger
      });
    }
    
    // Backup storage: sessionStorage
    if (isSessionStorageAvailable()) {
      sessionStorage.setItem("auth_token_backup", token);
      sessionStorage.setItem("auth_token_data_backup", serializedData);
      logCredentialDebug("TOKEN_STORED_SESSION", { 
        tokenLength: token.length, 
        hasBackup: true,
        skipVerificationTrigger
      });
    }
  } catch (error) {
    logCredentialDebug("TOKEN_STORAGE_ERROR", { 
      error: error instanceof Error ? error.message : String(error) 
    });
  }
}

/**
 * Enhanced token retrieval with integrity checking and fallback
 */
function retrieveToken(): string | null {
  try {
    // Try primary storage first
    if (isLocalStorageAvailable()) {
      const token = localStorage.getItem("auth_token");
      const tokenData = localStorage.getItem("auth_token_data");
      
      if (token && tokenData) {
        try {
          const parsed = JSON.parse(tokenData);
          // Basic integrity check
          if (parsed.token === token && parsed.integrity === token.length) {
            logCredentialDebug("TOKEN_RETRIEVED_LOCAL", { 
              tokenLength: token.length, 
              integrityValid: true,
              age: Date.now() - parsed.timestamp,
              skipVerificationTrigger: parsed.skipVerificationTrigger
            });
            return token;
          } else {
            logCredentialDebug("TOKEN_INTEGRITY_FAILED", { 
              tokenLength: token.length,
              expectedLength: parsed.integrity,
              tokensMatch: parsed.token === token
            });
          }
        } catch (parseError) {
          logCredentialDebug("TOKEN_DATA_PARSE_ERROR", { 
            error: parseError instanceof Error ? parseError.message : String(parseError) 
          });
        }
        
        // Return raw token if data parsing fails but token exists
        if (token) {
          logCredentialDebug("TOKEN_RETRIEVED_LOCAL_FALLBACK", { tokenLength: token.length });
          return token;
        }
      }
    }
    
    // Try backup storage
    if (isSessionStorageAvailable()) {
      const backupToken = sessionStorage.getItem("auth_token_backup");
      if (backupToken) {
        logCredentialDebug("TOKEN_RETRIEVED_SESSION_BACKUP", { tokenLength: backupToken.length });
        // Restore to primary storage if available (but don't trigger verification)
        if (isLocalStorageAvailable()) {
          localStorage.setItem("auth_token", backupToken);
          logCredentialDebug("TOKEN_RESTORED_TO_LOCAL", { source: "sessionStorage" });
        }
        return backupToken;
      }
    }
    
    logCredentialDebug("TOKEN_NOT_FOUND", { 
      localAvailable: isLocalStorageAvailable(),
      sessionAvailable: isSessionStorageAvailable()
    });
    return null;
    
  } catch (error) {
    logCredentialDebug("TOKEN_RETRIEVAL_ERROR", { 
      error: error instanceof Error ? error.message : String(error) 
    });
    return null;
  }
}

/**
 * Enhanced token removal with comprehensive cleanup
 */
function removeToken(): void {
  try {
    if (isLocalStorageAvailable()) {
      localStorage.removeItem("auth_token");
      localStorage.removeItem("auth_token_data");
      logCredentialDebug("TOKEN_REMOVED_LOCAL", { success: true });
    }
    
    if (isSessionStorageAvailable()) {
      sessionStorage.removeItem("auth_token_backup");
      sessionStorage.removeItem("auth_token_data_backup");
      logCredentialDebug("TOKEN_REMOVED_SESSION", { success: true });
    }

    // CRITICAL FIX: Clean up verification state when token is removed
    cleanupAllVerification();
  } catch (error) {
    logCredentialDebug("TOKEN_REMOVAL_ERROR", { 
      error: error instanceof Error ? error.message : String(error) 
    });
  }
}

/**
 * Authentication Provider Component
 * 
 * Wraps the application to provide authentication context.
 * Automatically verifies stored tokens on mount with enhanced retry logic.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // CRITICAL FIX: Use ref to track component instance and prevent stale closures
  const instanceRef = useRef(Symbol('auth-instance'));
  const currentInstance = instanceRef.current;

  // Check for existing token on mount and initialize debugging
  useEffect(() => {
    logCredentialDebug("AUTH_PROVIDER_INIT", { 
      timestamp: new Date().toISOString(),
      localStorageAvailable: isLocalStorageAvailable(),
      sessionStorageAvailable: isSessionStorageAvailable(),
      instanceId: currentInstance.toString(),
      currentVerificationState: globalVerificationState,
      detectedApiBaseUrl: getApiBaseUrl()
    });
    
    // CRITICAL FIX: Clean up any existing verification attempts before starting new one
    cleanupAllVerification();
    
    const storedToken = retrieveToken();
    if (storedToken) {
      logCredentialDebug("STORED_TOKEN_FOUND", { 
        tokenLength: storedToken.length,
        instanceId: currentInstance.toString()
      });
      verifyTokenWithRetry(storedToken, currentInstance);
    } else {
      logCredentialDebug("NO_STORED_TOKEN", { checkComplete: true });
      setIsLoading(false);
    }

    // Cleanup on unmount
    return () => {
      logCredentialDebug("AUTH_PROVIDER_CLEANUP", { 
        instanceId: currentInstance.toString()
      });
      cleanupAllVerification();
    };
  }, []);

  /**
   * Helper function to safely clear loading state and cleanup
   */
  const clearLoadingState = (reason: string, instanceId?: symbol) => {
    // Only clear if this is the current instance
    if (instanceId && instanceId !== currentInstance) {
      logCredentialDebug("LOADING_STATE_SKIPPED_STALE_INSTANCE", { 
        reason,
        instanceId: instanceId.toString(),
        currentInstance: currentInstance.toString()
      });
      return;
    }

    logCredentialDebug("LOADING_STATE_CLEARED", { 
      reason,
      timestamp: new Date().toISOString(),
      previouslyLoading: isLoading,
      instanceId: currentInstance.toString()
    });
    
    setIsLoading(false);
  };

  /**
   * CRITICAL FIX: Completely rewritten token verification to prevent accumulation
   * 
   * Key fixes:
   * - Uses module-level state that persists across renders
   * - Tracks verification instance to prevent stale callbacks
   * - Immediate timeout cleanup and debouncing
   * - No re-storage of tokens to prevent verification loops
   * - Single verification attempt per authentication session
   * - Environment-aware API URL construction
   */
  const verifyTokenWithRetry = async (tokenToVerify: string, instanceId: symbol, attempt: number = 1): Promise<void> => {
    const maxRetries = 2;
    const retryDelay = attempt * 1000;
    const verificationId = `${Date.now()}-${Math.random()}`;
    
    // CRITICAL FIX: Strict debouncing with cooldown
    const now = Date.now();
    const timeSinceLastVerification = now - globalVerificationState.lastVerificationTime;
    const minimumCooldown = 1000; // 1 second minimum between verification attempts

    if (globalVerificationState.isVerifying || timeSinceLastVerification < minimumCooldown) {
      logCredentialDebug("TOKEN_VERIFICATION_DEBOUNCED", {
        attempt,
        reason: globalVerificationState.isVerifying ? "already_verifying" : "cooldown_active",
        timeSinceLastVerification,
        minimumCooldown,
        currentVerificationId: globalVerificationState.currentVerificationId
      });
      clearLoadingState("verification_debounced", instanceId);
      return;
    }

    // Set verification state
    globalVerificationState.isVerifying = true;
    globalVerificationState.currentVerificationId = verificationId;
    globalVerificationState.lastVerificationTime = now;
    
    // Construct the full API URL
    const apiBaseUrl = getApiBaseUrl();
    const fullUrl = `${apiBaseUrl}/api/auth/verify`;
    
    logCredentialDebug("TOKEN_VERIFICATION_ATTEMPT", {
      attempt,
      maxRetries,
      tokenLength: tokenToVerify.length,
      retryDelay,
      verificationId,
      instanceId: instanceId.toString(),
      apiBaseUrl,
      fullUrl
    });
    
    // Enhanced API call logging
    logApiCall("GET", "/api/auth/verify", {
      attempt,
      verificationId,
      tokenLength: tokenToVerify.length
    });
    
    try {
      const response = await fetch(fullUrl, {
        method: "GET",
        headers: {
          "Authorization": `Bearer ${tokenToVerify}`,
          "Content-Type": "application/json",
        },
      });

      // Check if this instance is still current
      if (instanceId !== currentInstance) {
        logCredentialDebug("TOKEN_VERIFICATION_STALE_INSTANCE", {
          verificationId,
          instanceId: instanceId.toString(),
          currentInstance: currentInstance.toString()
        });
        return;
      }

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.user) {
          logCredentialDebug("TOKEN_VERIFICATION_SUCCESS", {
            attempt,
            userId: data.user.id,
            userName: data.user.name,
            userRole: data.user.role,
            verificationId,
            apiUrl: fullUrl
          });
          
          setUser(data.user);
          setToken(tokenToVerify);
          
          // CRITICAL FIX: Store token but skip verification trigger to prevent loops
          storeToken(tokenToVerify, true);
          
          // BRIDGE 3: Initialize platform tracking after successful token verification
          try {
            const trackingInitialized = initializeTrackingWithAuth(tokenToVerify);
            logCredentialDebug("PLATFORM_TRACKING_INITIALIZATION_VERIFY", {
              success: trackingInitialized,
              userId: data.user.id,
              authTokenLength: tokenToVerify.length,
              verificationId
            });
          } catch (trackingError) {
            logCredentialDebug("PLATFORM_TRACKING_INITIALIZATION_ERROR_VERIFY", {
              error: trackingError instanceof Error ? trackingError.message : String(trackingError),
              userId: data.user.id,
              verificationId
            });
            // Don't fail verification if tracking initialization fails
          }
          
          // Reset verification state
          globalVerificationState.isVerifying = false;
          globalVerificationState.currentVerificationId = null;
          
          clearLoadingState("verification_success", instanceId);
          return;
        } else {
          // Token is invalid
          logCredentialDebug("TOKEN_INVALID_SERVER_RESPONSE", {
            attempt,
            success: data.success,
            hasUser: !!data.user,
            reason: "server_rejected_token",
            verificationId,
            apiUrl: fullUrl
          });
          removeToken();
          setUser(null);
          setToken(null);
          clearLoadingState("token_invalid", instanceId);
          return;
        }
      } else {
        // HTTP error
        const isClientError = response.status >= 400 && response.status < 500;
        const isServerError = response.status >= 500;
        
        logCredentialDebug("TOKEN_VERIFICATION_HTTP_ERROR", {
          attempt,
          status: response.status,
          statusText: response.statusText,
          isClientError,
          isServerError,
          willRetry: !isClientError && attempt < maxRetries,
          verificationId,
          apiUrl: fullUrl
        });
        
        if (isClientError) {
          // Client error means token is invalid
          logCredentialDebug("TOKEN_INVALID_CLIENT_ERROR", {
            status: response.status,
            reason: "client_error_token_rejected",
            verificationId,
            apiUrl: fullUrl
          });
          removeToken();
          setUser(null);
          setToken(null);
          clearLoadingState("client_error", instanceId);
          return;
        } else if (isServerError && attempt < maxRetries) {
          // Server error - retry after delay
          logCredentialDebug("TOKEN_VERIFICATION_RETRY_SCHEDULED", {
            attempt,
            nextAttempt: attempt + 1,
            delay: retryDelay,
            reason: "server_error",
            verificationId,
            apiUrl: fullUrl
          });
          
          // Reset verification state before retry
          globalVerificationState.isVerifying = false;
          globalVerificationState.currentVerificationId = null;
          
          const timeout = setTimeout(() => {
            globalVerificationState.pendingTimeouts.delete(timeout);
            verifyTokenWithRetry(tokenToVerify, instanceId, attempt + 1);
          }, retryDelay);
          
          globalVerificationState.pendingTimeouts.add(timeout);
          return;
        } else {
          // Max retries exceeded
          logCredentialDebug("TOKEN_VERIFICATION_MAX_RETRIES", {
            attempt,
            maxRetries,
            finalStatus: response.status,
            reason: "max_retries_exceeded",
            verificationId,
            apiUrl: fullUrl
          });
          
          // Reset verification state
          globalVerificationState.isVerifying = false;
          globalVerificationState.currentVerificationId = null;
          
          if (isServerError) {
            // Keep token but clear user state for server errors
            setUser(null);
          } else {
            removeToken();
            setUser(null);
            setToken(null);
          }
          
          clearLoadingState("max_retries_exceeded", instanceId);
          return;
        }
      }
    } catch (error) {
      // Network error
      const errorMessage = error instanceof Error ? error.message : String(error);
      
      logCredentialDebug("TOKEN_VERIFICATION_EXCEPTION", {
        attempt,
        error: errorMessage,
        willRetry: attempt < maxRetries,
        verificationId,
        apiUrl: fullUrl
      });
      
      if (attempt < maxRetries) {
        // Reset verification state before retry
        globalVerificationState.isVerifying = false;
        globalVerificationState.currentVerificationId = null;
        
        const timeout = setTimeout(() => {
          globalVerificationState.pendingTimeouts.delete(timeout);
          verifyTokenWithRetry(tokenToVerify, instanceId, attempt + 1);
        }, retryDelay);
        
        globalVerificationState.pendingTimeouts.add(timeout);
        return;
      } else {
        // Max retries exceeded for network error
        logCredentialDebug("TOKEN_VERIFICATION_NETWORK_MAX_RETRIES", {
          attempt,
          maxRetries,
          reason: "network_error_preserving_token",
          verificationId,
          apiUrl: fullUrl
        });
        
        // Reset verification state
        globalVerificationState.isVerifying = false;
        globalVerificationState.currentVerificationId = null;
        
        // Keep token for future retry
        setUser(null);
        clearLoadingState("network_error_max_retries", instanceId);
        return;
      }
    }
  };

  /**
   * CRITICAL FIX: Enhanced login with rapid retry handling and connection resilience
   * Now with environment-aware API URL construction
   */
  const login = async (email: string, password: string) => {
    try {
      // CRITICAL FIX: Clean up all existing verification before new login
      cleanupAllVerification();
      
      setIsLoading(true);
      
      // Construct the full API URL
      const apiBaseUrl = getApiBaseUrl();
      const fullUrl = `${apiBaseUrl}/api/auth/login`;
      
      logCredentialDebug("LOGIN_ATTEMPT", { 
        email, 
        timestamp: new Date().toISOString(),
        instanceId: currentInstance.toString(),
        apiBaseUrl,
        fullUrl
      });
      
      // Enhanced API call logging
      logApiCall("POST", "/api/auth/login", {
        email,
        fullUrl
      });
      
      const response = await fetch(fullUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();

      // Handle success
      if (data.success && data.token && data.user) {
        logCredentialDebug("LOGIN_SUCCESS", {
          userId: data.user.id,
          userName: data.user.name,
          userRole: data.user.role,
          tokenLength: data.token.length,
          instanceId: currentInstance.toString(),
          apiUrl: fullUrl
        });
        
        setUser(data.user);
        setToken(data.token);
        
        // CRITICAL FIX: Store token without triggering verification (we just verified via login)
        storeToken(data.token, true);
        
        // BRIDGE 3: Initialize platform tracking with auth token after successful login
        try {
          const trackingInitialized = initializeTrackingWithAuth(data.token);
          logCredentialDebug("PLATFORM_TRACKING_INITIALIZATION", {
            success: trackingInitialized,
            userId: data.user.id,
            authTokenLength: data.token.length
          });
        } catch (trackingError) {
          logCredentialDebug("PLATFORM_TRACKING_INITIALIZATION_ERROR", {
            error: trackingError instanceof Error ? trackingError.message : String(trackingError),
            userId: data.user.id
          });
          // Don't fail login if tracking initialization fails
        }
        
        return { success: true };
      } 
      
      // Handle service busy (503) - special case for rapid retries
      else if (response.status === 503) {
        logCredentialDebug("LOGIN_SERVICE_BUSY", {
          email,
          status: response.status,
          error: data.error,
          instanceId: currentInstance.toString(),
          apiUrl: fullUrl
        });
        
        return { 
          success: false, 
          error: "Please wait a moment before trying again. The service is temporarily busy." 
        };
      } 
      
      // Handle other errors
      else {
        logCredentialDebug("LOGIN_FAILED", {
          email,
          status: response.status,
          error: data.error,
          hasToken: !!data.token,
          hasUser: !!data.user,
          instanceId: currentInstance.toString(),
          apiUrl: fullUrl
        });
        
        // Provide more specific error messages based on status
        let errorMessage = data.error || "Login failed";
        
        if (response.status === 401) {
          errorMessage = data.error || "Invalid email or password";
        } else if (response.status >= 500) {
          errorMessage = "Server error. Please try again in a moment.";
        }
            
        return { success: false, error: errorMessage };
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      logCredentialDebug("LOGIN_EXCEPTION", {
        email,
        error: errorMessage,
        instanceId: currentInstance.toString(),
        attemptedUrl: `${getApiBaseUrl()}/api/auth/login`
      });
      
      // Check if this looks like a network timeout or connection error
      const isNetworkError = errorMessage.includes('fetch') || 
                            errorMessage.includes('network') || 
                            errorMessage.includes('timeout') ||
                            errorMessage.includes('Failed to fetch');
      
      if (isNetworkError) {
        return { 
          success: false, 
          error: "Connection timeout. Please check your internet and try again." 
        };
      }
      
      return { success: false, error: "Network error. Please try again." };
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * CRITICAL FIX: Enhanced register with immediate verification cleanup
   * Now with environment-aware API URL construction
   * UPDATED: Now accepts optional role parameter for demo signups
   */
  const register = async (
    name: string, 
    email: string, 
    password: string,
    role: "user" | "preview" = "user"
  ) => {
    try {
      // CRITICAL FIX: Clean up all existing verification before new registration
      cleanupAllVerification();
      
      setIsLoading(true);
      
      // Construct the full API URL
      const apiBaseUrl = getApiBaseUrl();
      const fullUrl = `${apiBaseUrl}/api/auth/register`;
      
      logCredentialDebug("REGISTER_ATTEMPT", { 
        name, 
        email,
        role,
        timestamp: new Date().toISOString(),
        instanceId: currentInstance.toString(),
        apiBaseUrl,
        fullUrl
      });
      
      // Enhanced API call logging
      logApiCall("POST", "/api/auth/register", {
        name,
        email,
        role,
        fullUrl
      });
      
      const response = await fetch(fullUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ name, email, password, role }),
      });

      const data = await response.json();

      if (data.success && data.token && data.user) {
        logCredentialDebug("REGISTER_SUCCESS", {
          userId: data.user.id,
          userName: data.user.name,
          userRole: data.user.role,
          tokenLength: data.token.length,
          instanceId: currentInstance.toString(),
          apiUrl: fullUrl
        });
        
        setUser(data.user);
        setToken(data.token);
        
        // CRITICAL FIX: Store token without triggering verification (we just verified via registration)
        storeToken(data.token, true);
        
        // BRIDGE 3: Initialize platform tracking with auth token after successful registration
        try {
          const trackingInitialized = initializeTrackingWithAuth(data.token);
          logCredentialDebug("PLATFORM_TRACKING_INITIALIZATION_REGISTER", {
            success: trackingInitialized,
            userId: data.user.id,
            authTokenLength: data.token.length
          });
        } catch (trackingError) {
          logCredentialDebug("PLATFORM_TRACKING_INITIALIZATION_ERROR_REGISTER", {
            error: trackingError instanceof Error ? trackingError.message : String(trackingError),
            userId: data.user.id
          });
          // Don't fail registration if tracking initialization fails
        }
        
        return { success: true };
      } else {
        logCredentialDebug("REGISTER_FAILED", {
          name,
          email,
          role,
          error: data.error,
          hasToken: !!data.token,
          hasUser: !!data.user,
          instanceId: currentInstance.toString(),
          apiUrl: fullUrl
        });
        return { success: false, error: data.error || "Registration failed" };
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      logCredentialDebug("REGISTER_EXCEPTION", {
        name,
        email,
        role,
        error: errorMessage,
        instanceId: currentInstance.toString(),
        attemptedUrl: `${getApiBaseUrl()}/api/auth/register`
      });
      return { success: false, error: "Network error. Please try again." };
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * CRITICAL FIX: Enhanced logout with complete verification cleanup AND analytics session cleanup
   */
  const logout = async () => {
    logCredentialDebug("LOGOUT", { 
      hadUser: !!user,
      hadToken: !!token,
      timestamp: new Date().toISOString(),
      instanceId: currentInstance.toString()
    });
    
    // CRITICAL FIX: Clean up all verification attempts before logout
    cleanupAllVerification();
    
    // CRITICAL FIX: Clean up analytics session state to prevent cross-user session reuse
    try {
      const { getCurrentSessionId, endSession: endAnalyticsSession } = await import('@/lib/analytics/client');
      const sessionId = getCurrentSessionId();
      if (sessionId) {
        logCredentialDebug("LOGOUT_ENDING_SESSION", { sessionId });
        await endAnalyticsSession(sessionId, {
          totalDuration: 0,
          pagesVisited: [],
          finalInteractions: 0,
          cuesCollected: []
        });
      } else {
        logCredentialDebug("LOGOUT_NO_SESSION_TO_END", {});
      }
    } catch (analyticsError) {
      logCredentialDebug("LOGOUT_ANALYTICS_CLEANUP_ERROR", { 
        error: analyticsError instanceof Error ? analyticsError.message : String(analyticsError) 
      });
      // Don't fail logout if analytics cleanup fails
    }
    
    setUser(null);
    setToken(null);
    removeToken(); // This also calls cleanupAllVerification()
    setIsLoading(false);
  };

  const value: AuthContextType = {
    user,
    token,
    isLoading,
    login,
    register,
    logout,
    isAuthenticated: !!user,
    isAdmin: user?.role === "admin",
    isPreview: user?.role === "preview",
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

/**
 * Hook to use authentication context
 * 
 * Must be used within an AuthProvider component.
 * Provides access to authentication state and methods.
 */
export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
